import { useNavigateWithSearchParams } from "@p0security/frontend-core";
import { useFirestoreDoc } from "@p0security/frontend-core/react";
import { AwsIntegration } from "@p0security/integrations-aws-shared/types";
import { GcloudIntegration } from "@p0security/integrations-gcloud-shared/types";
import { installedItems } from "@p0security/shared/install/installed";
import { AppPaths } from "@p0security/shared/routes/constants";
import {
  Button,
  Card,
  Col,
  Divider,
  Flex,
  Image,
  Row,
  Space,
  Spacing,
  Typography,
} from "@p0security/ui";
import { JitPaths } from "components/Jit/paths";
import { Splash } from "components/Splash";
import { TenantAwareLink } from "components/common/TenantAwareLink";
import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import { spacing } from "styles/spacing";
import { useQueryParam } from "use-query-params";

import { AllIntegrationLogos } from "../../Integrations/IntegrationLogo.tsx";
import { Tenant } from "../../Login";
import { SkipOnboardingButton } from "../components/SkipOnboardingButton";
import { WelcomePaths } from "../paths";
import { StyledCol } from "../styles";
import { SupportedCloudProviders } from "../types";

const { Title } = Typography;

const DEMO_URLS: Record<SupportedCloudProviders, string> = {
  aws: "/aws-policy.gif",
  gcloud: "/gcloud-resource.gif",
  azure: "/aws-policy.gif", // TODO ENG-4897 update with Azure gif once we have one
};

const ContentCard: React.FC<
  PropsWithChildren<{
    title?: React.ReactNode;
    imageSrc: string;
    imageHeight?: number;
    imageWidth?: number | string;
    orgSlug?: string;
  }>
> = ({
  title,
  imageSrc,
  imageHeight,
  imageWidth = "100%",
  orgSlug,
  children,
}) => (
  <Card title={title} variant="borderless">
    <Image
      preview={false}
      src={imageSrc}
      alt="P0"
      height={imageHeight}
      width={imageWidth}
    />
    {children}
    <Divider />
    <SkipOnboardingButton textOverride="Complete the guided onboarding" />
    or
    {/* We want to clear the search params if they go back to the start */}
    {/* nosemgrep:link-component-in-environment-folders */}
    <Link to={`/o/${orgSlug}/${WelcomePaths.Root}/`}>
      Add Additional Resources &nbsp;
    </Link>
  </Card>
);

const Success: React.FC = React.memo(() => {
  const navigateWithSearchParams = useNavigateWithSearchParams();
  const { orgSlug, cloudProvider } = useParams<{
    orgSlug?: string;
    cloudProvider?: SupportedCloudProviders;
  }>();
  const [flow] = useQueryParam("flow");

  const tenantId = useContext(Tenant);
  const { doc: awsDoc } = useFirestoreDoc<AwsIntegration>(
    `o/${tenantId}/integrations/aws`,
    { live: true }
  );

  const { doc: gcloudDoc } = useFirestoreDoc<GcloudIntegration>(
    `o/${tenantId}/integrations/gcloud`,
    { live: true }
  );

  const installedProjectId = useMemo(() => {
    const doc = awsDoc || gcloudDoc;
    if (!doc) return undefined;

    const auditItems = installedItems("iam-assessment", doc.data);
    return Object.keys(auditItems)[0];
  }, [awsDoc, gcloudDoc]);

  const demoUrl = useMemo(() => {
    if (!cloudProvider || !DEMO_URLS[cloudProvider]) {
      return DEMO_URLS.aws; // Default fallback
    }
    return DEMO_URLS[cloudProvider];
  }, [cloudProvider]);

  const handleStartAuditClick = useCallback(() => {
    if (!installedProjectId || !orgSlug) return;
    navigateWithSearchParams(`/o/${orgSlug}/${AppPaths.Settings}`);
  }, [navigateWithSearchParams, orgSlug, installedProjectId]);

  return (
    <Splash overrideColumn>
      <Col xs={22} md={16} lg={14} xl={12}>
        <Row gutter={[24, 24]}>
          <Col xs={24}>
            {
              <Title level={4}>
                Congratulations, you&apos;re ready to{" "}
                {flow === "assessment"
                  ? "run a posture and inventory scan for "
                  : "grant granular least-privileged access to "}
                {cloudProvider ? AllIntegrationLogos[cloudProvider] : ""}
              </Title>
            }
          </Col>
        </Row>
        <Row gutter={[24, 24]}>
          <StyledCol xs={24}>
            {flow === "assessment" ? (
              <ContentCard
                title={
                  <Flex align="center" justify="space-between">
                    Run your first scan
                    <Button
                      type="primary"
                      onClick={handleStartAuditClick}
                      disabled={!installedProjectId}
                    >
                      Let&apos;s get started!
                    </Button>
                  </Flex>
                }
                // ENG-4769: Add a demo gif for posture and inventory
                imageSrc="/posture.jpg"
                orgSlug={orgSlug}
              />
            ) : (
              <ContentCard
                imageSrc={demoUrl}
                imageHeight={380}
                imageWidth={480}
                orgSlug={orgSlug}
              >
                <Spacing asChild mt={spacing.scale.lg}>
                  <Space
                    /* eslint-disable-line react/forbid-component-props */ style={{
                      justifyContent: "center",
                      width: "100%",
                    }}
                  >
                    <TenantAwareLink
                      to={`${AppPaths.AccessManagement}/${JitPaths.Activity}`}
                    >
                      <Button type="primary">Get Started</Button>
                    </TenantAwareLink>
                  </Space>
                </Spacing>
              </ContentCard>
            )}
          </StyledCol>
        </Row>
      </Col>
    </Splash>
  );
});

Success.displayName = "Success";

export { Success };
